import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { apiStore } from '@/stores/ApiStore';
import { CtaButton } from './CtaButton';
import { ROUTES } from '@/constants/routes';

const ContactFormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 16px;
  flex: 1;
`;

const Label = styled.label`
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  letter-spacing: -0.41px;
  text-transform: uppercase;
  color: #212121;
  margin-bottom: 8px;
`;

const Textarea = styled.textarea<{ $error?: boolean }>`
  width: 100%;
  padding: 15px 12px;
  border-radius: 8px;
  outline: none;
  font-family: inherit;
  background-color: ${({ $error }) => ($error ? '#fdf2f2' : '#f5f5f5')};
  border: ${({ $error }) => ($error ? '1px solid #e74c3c' : 'none')};
  font-weight: 400;
  font-size: 17px;
  line-height: 21px;
  letter-spacing: -0.32px;
  resize: none;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    background-color: ${({ $error }) => ($error ? '#fdf2f2' : '#ffffff')};
    box-shadow: 0 0 0 2px ${({ $error }) => ($error ? '#e74c3c' : '#31bcf9')};
  }
`;

const CtaSection = styled.div`
  margin-top: auto;
  margin-bottom: 24px;
`;

const ErrorText = styled.div`
  font-size: 12px;
  color: #e74c3c;
  margin-top: 4px;
  margin-bottom: 8px;
`;

interface ContactFormProps {
  ctaLabel?: string;
}

export function ContactForm({ ctaLabel = 'Send Feedback' }: ContactFormProps) {
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);
  const msgRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    msgRef.current?.focus();
  }, []);

  const validateMessage = (value: string): string => {
    if (!value.trim()) {
      return 'Message is required';
    }
    if (value.trim().length < 1) {
      return 'Message must be at least 1 character long';
    }
    return '';
  };

  const handleChange = (value: string) => {
    setMessage(value);
    if (touched) {
      setError(validateMessage(value));
    }
  };

  const handleBlur = () => {
    setTouched(false);
    setError(validateMessage(message));
  };

  const canSend = !error && message.trim().length > 0;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);

    const validationError = validateMessage(message);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (!canSend) return;

    try {
      await apiStore.submitFeedback(message);
      navigate(ROUTES.POLICY.INDEX);
    }
    catch {
      // Error handled
    }

    setMessage('');
    setError('');
    setTouched(false);
    msgRef.current?.blur();
  };

  return (
    <ContactFormWrapper>
      <Form onSubmit={onSubmit}>
        <Label htmlFor="message">How can we help you?</Label>
        <Textarea
          id="message"
          ref={msgRef}
          value={message}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleBlur}
          rows={5}
          placeholder="Describe the problem to our team"
          $error={touched && !!error}
        />
        {touched && error && <ErrorText>{error}</ErrorText>}

        {canSend && (
          <CtaSection>
            <CtaButton label={ctaLabel} type="submit" />
          </CtaSection>
        )}
      </Form>
    </ContactFormWrapper>
  );
}
